import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { BrandGroup } from '@provision/entity-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader } from 'src/components/Loader/loader';
import { SAVE_DEBOUNCE_DELAY } from 'src/constants/delays';
import { useBrandGroup, useBrandGroups } from 'src/graphql/entities/brand-group';
import { useGlobalStore } from 'src/store';
import { ChannelsVoiceForm } from './channels-voice-form';
import { toVoiceBrandGroupPatch } from './to-voice-brand-group-patch';
import { VoiceFormModel } from './types';
import { useBrandGroupAutosave } from './use-brand-group-autosave';

export const ChannelsVoiceView = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { brandGroups } = useBrandGroups(selectedPartnerId);
  const [brandGroup, setBrandGroup] = useState<BrandGroup | null>(null);

  useEffect(() => {
    if (!brandGroups.length) {
      return;
    }

    setBrandGroup(brandGroups[0]);
  }, [brandGroups, setBrandGroup]);

  const { updateBrandGroup } = useBrandGroup(selectedPartnerId);
  const { unsaved, queueSave } = useBrandGroupAutosave({
    brandGroupId: brandGroup?.id,
    updateBrandGroup,
    delayMs: SAVE_DEBOUNCE_DELAY,
  });

  // Unsaved edits win over the server copy until a save of them settles.
  const displayedBrandGroup = useMemo(
    () => (brandGroup ? ({ ...brandGroup, ...unsaved } as BrandGroup) : null),
    [brandGroup, unsaved],
  );

  const handleSave = useCallback(
    (formValue: VoiceFormModel) => queueSave(toVoiceBrandGroupPatch(formValue)),
    [queueSave],
  );

  const handleBrandGroupChange = useCallback(
    (brandGroupId: string) => {
      const selectedBrandGroup = brandGroups.find((item) => item.id === brandGroupId);
      setBrandGroup(selectedBrandGroup!);
    },
    [brandGroups, setBrandGroup],
  );

  return brandGroups?.length ? (
    <Stack gap={1}>
      {brandGroups?.length > 1 ? (
        <FormControl
          sx={{
            width: 'max-content',
          }}
        >
          <InputLabel id="brand-group-label">Brand Group</InputLabel>
          <Select
            labelId="brand-group-label"
            value={brandGroup?.id ?? ''}
            id="settings-email-brand-group"
            label="Brand Group"
            onChange={(event) => {
              handleBrandGroupChange(event.target.value);
            }}
          >
            {brandGroups.map((brandGroup) => (
              <MenuItem key={brandGroup.id} value={brandGroup.id}>
                {brandGroup.brandGroupName || 'Not defined'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {brandGroup ? (
        <Stack p={1} gap={1} sx={{ maxWidth: 600 }}>
          <Typography variant="subtitle1" sx={{ marginBottom: '1rem' }}>
            Configure the phone numbers used for support
          </Typography>
          <ChannelsVoiceForm
            handleSave={handleSave}
            selectedBrandGroup={displayedBrandGroup}
          />
        </Stack>
      ) : null}
    </Stack>
  ) : (
    <Loader />
  );
};
